import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { Table } from 'dashboard/models/external-integration';
import {
  Importer,
  ImporterArchiveAfterCompletion,
  ImporterArraySyncMode,
  ImporterRunType,
  ImporterStatus,
  ImporterSyncMode,
  isImporterActive,
  MANUAL_SCHEDULE_STATE,
} from 'dashboard/models/importer';
import StepperFormControlLabel from 'dashboard/views/DashboardImporterStepperView/StepperFormControlLabel';
import { EditImporterData } from 'dashboard/views/DashboardImporterView/types';
import Card from 'design-system/components/Card';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import Tooltip from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';

const HeaderContainer = styled.div`
  color: ${({ theme }) => theme.color.text.primary};
  max-width: 700px;
  display: flex;
`;

const Header = styled.div`
  margin-right: ${Spacing[2]};
`;

const Description = styled.div`
  ${paragraphMixins.md}
  color: ${({ theme }) => theme.color.text.secondary};
  margin-bottom: ${Spacing[1]};
  max-width: 700px;
`;

const SelectWrapper = styled.div`
  max-width: 400px;
`;

const StyledIconInfoCircle = styled(CircleInfoIcon)`
  margin-top: ${Spacing[1]};
  path,
  rect {
    stroke: ${({ theme }) => theme.color.text.secondary};
  }
`;

const ToolTipDescriptionContainer = styled.div`
  max-width: 480px;
`;

interface DashboardImporterViewSettingsTabProps {
  form: UseFormReturn<EditImporterData>;
  schema: Table;
  canEditSchedule: boolean;
  canEditCursors: boolean;
  importer: Importer;
  isRecurrable: boolean;
  destination: 'Account' | 'Txn';
}

const DashboardImporterViewSettingsTab = ({
  form,
  schema,
  canEditSchedule,
  canEditCursors,
  importer,
  isRecurrable,
  destination,
}: DashboardImporterViewSettingsTabProps) => {
  const {
    control,
    register,
    formState: { errors },
    watch,
  } = form;
  const updatedAtCursor = watch('updatedAtCursor');
  const primaryIdCursor = watch('primaryIdCursor');

  const cursorOptions =
    schema &&
    schema.columns.map(
      (column: {
        name: string;
        type: string;
        preview?: string | number | boolean | null | undefined;
      }) => {
        return { label: column.name, value: column.name };
      },
    );

  const scheduleInterval = watch('scheduleInterval');
  const syncMode = watch('syncMode');
  // import.meta.env.PROD is true for both staging and production environments
  const isStaging =
    import.meta.env.PROD && window.location.hostname.includes('withpersona-staging');
  const scheduleOptions = [
    // When a recurring importer is activated, instead of surfacing the "Never" radio option
    // force them to deactivate the importer to stop the schedule.
    !isImporterActive(importer) || importer.attributes['run-type'] !== ImporterRunType.Recurring
      ? [{ label: 'Never', value: MANUAL_SCHEDULE_STATE }]
      : [],
    // for ease of testing, add some short intervals
    import.meta.env.DEV || isStaging
      ? [
          { label: '2 minutes', value: (2 * 60).toString() },
          { label: '5 minutes', value: (5 * 60).toString() },
        ]
      : [],
    { label: '12 hours', value: (12 * 3600).toString() },
    { label: '24 hours', value: (24 * 3600).toString() },
    { label: 'Weekly', value: (7 * 24 * 3600).toString() },
  ].flat();

  const destinationName = destination
    ? destination === 'Account'
      ? 'Account'
      : 'Transaction'
    : 'Object';
  const syncModeOptions: OptionType<ImporterSyncMode>[] = [
    { label: `Create new or update existing ${destinationName}s`, value: ImporterSyncMode.Upsert },
    { label: `Create new ${destinationName}s only`, value: ImporterSyncMode.Insert },
    { label: `Update existing ${destinationName}s only`, value: ImporterSyncMode.Update },
  ];

  const arraySyncModeOptions: OptionType<ImporterArraySyncMode>[] = [
    { label: 'Append to existing array', value: ImporterArraySyncMode.Append },
    { label: 'Replace entire array', value: ImporterArraySyncMode.Replace },
  ];

  const archiveAfterCompletionOptions: OptionType<ImporterArchiveAfterCompletion>[] = [
    { label: 'Yes, deactivate after completion.', value: ImporterArchiveAfterCompletion.True },
    { label: 'No, keep after completion.', value: ImporterArchiveAfterCompletion.False },
  ];

  return (
    <div>
      <Card>
        <Card.Body>
          <Card.Title>Settings</Card.Title>
          <Card.SectionHeader>Destination action</Card.SectionHeader>
          <RadioGroup>
            {syncModeOptions.map((option) => {
              return (
                <Radio
                  key={option.value}
                  label={option.label}
                  {...register('syncMode')}
                  value={option.value}
                  disabled={importer.attributes.status !== ImporterStatus.Inactive}
                />
              );
            })}
          </RadioGroup>
          <FeatureFlagged featureFlag='dashboard:importers-enable-csv-arrays-using-json'>
            <Spacer height={1} />
            <Card.SectionHeader>Array column behavior</Card.SectionHeader>
            <RadioGroup>
              {arraySyncModeOptions.map((option) => {
                return (
                  <Radio
                    key={option.value}
                    label={option.label}
                    {...register('arraySyncMode')}
                    value={option.value}
                    disabled={
                      syncMode === ImporterSyncMode.Insert ||
                      importer.attributes.status !== ImporterStatus.Inactive
                    }
                  />
                );
              })}
            </RadioGroup>
          </FeatureFlagged>
          {isRecurrable && (
            <>
              <Spacer height={1} />
              <Card.SectionHeader>Schedule Configuration</Card.SectionHeader>
              <Description>
                Define the required fields below to keep future data imports clean and organized.
              </Description>
              <HeaderContainer>
                <Header>updated_at</Header>
                <Tooltip side='right' iconTrigger={<StyledIconInfoCircle size={12} />}>
                  <ToolTipDescriptionContainer>
                    The updated_at field is used to indicate what rows have been modified.
                  </ToolTipDescriptionContainer>
                </Tooltip>
              </HeaderContainer>
              <Controller
                control={control}
                defaultValue={updatedAtCursor}
                name='updatedAtCursor'
                // if scheduleInterval is present, then the importer is recurring, so we require
                // that at least the updated_at cursor is populated
                rules={{
                  required: scheduleInterval != null && scheduleInterval !== MANUAL_SCHEDULE_STATE,
                }}
                render={({ field: { onChange, value } }) => {
                  return (
                    <SelectWrapper>
                      <ReactSelect
                        id='dashboard-importer-view__updated_at_cursor'
                        aria-label='Updated At Cursor'
                        isClearable={true}
                        value={value as string}
                        onChange={(value) => {
                          onChange(value);
                        }}
                        disabled={!canEditCursors}
                        // don't let updatedAtCursor and primaryIdCursor be the same
                        options={cursorOptions.filter((o) => o.value !== primaryIdCursor)}
                        placeholder='Select Field'
                        portal={true}
                      />
                    </SelectWrapper>
                  );
                }}
              />
              {errors['updatedAtCursor'] && (
                <FormError>A Cursor is required for scheduling a recurring import</FormError>
              )}
              <Spacer height={1} />
              <HeaderContainer>
                <Header>primary_id</Header>
                <Tooltip side='right' iconTrigger={<StyledIconInfoCircle size={12} />}>
                  <ToolTipDescriptionContainer>
                    A primary_id is a unique identifier for each row in a data source. It is
                    recommended to index your data source by your updated_at field, and then your
                    primary_id field if possible.
                  </ToolTipDescriptionContainer>
                </Tooltip>
              </HeaderContainer>
              <Controller
                control={control}
                defaultValue={primaryIdCursor}
                name='primaryIdCursor'
                render={({ field: { onChange, value } }) => {
                  return (
                    <SelectWrapper>
                      <ReactSelect
                        id='dashboard-importer-view__primary_id_cursor'
                        aria-label='Primary Id Cursor'
                        isClearable={true}
                        value={value as string}
                        onChange={(value) => {
                          onChange(value);
                        }}
                        disabled={!canEditCursors}
                        // don't let updatedAtCursor and primaryIdCursor be the same
                        options={cursorOptions.filter((o) => o.value !== updatedAtCursor)}
                        placeholder='Select Field'
                        portal={true}
                      />
                    </SelectWrapper>
                  );
                }}
              />
              <Spacer height={1} />
              <Header>Importer will run every</Header>
              <RadioGroup>
                {scheduleOptions.map((option) => {
                  return (
                    <Radio
                      key={option.value}
                      label={option.label}
                      {...register('scheduleInterval')}
                      value={option.value}
                      disabled={!canEditSchedule}
                    />
                  );
                })}
              </RadioGroup>
              <Description>
                This importer will run immediately after saving and the schedule will come into
                effect.
              </Description>
            </>
          )}
          <Spacer height={1} />
          <Card.SectionHeader>Archive</Card.SectionHeader>
          <StepperFormControlLabel htmlFor='archiveAfterCompletion'>
            Delete data source and Importer after use?
          </StepperFormControlLabel>
          <RadioGroup>
            {archiveAfterCompletionOptions.map((option) => {
              return (
                <Radio
                  key={option.value}
                  label={option.label}
                  {...register('archiveAfterCompletion', {
                    validate: (value) =>
                      value === ImporterArchiveAfterCompletion.False ||
                      scheduleInterval === MANUAL_SCHEDULE_STATE,
                  })}
                  value={option.value}
                  disabled={importer.attributes.status !== ImporterStatus.Inactive}
                />
              );
            })}
          </RadioGroup>
          {errors['archiveAfterCompletion'] && (
            <FormError>
              Cannot automatically deactivate importer after completion if it is recurring.
            </FormError>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DashboardImporterViewSettingsTab;
